import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { subscribeJob, subscribeStops, STOP_STATUS_META } from "../lib/deliveries";

function fmt(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return "—"; } }

export default function JobReport() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [stops, setStops] = useState([]);
  useEffect(() => { const u1 = subscribeJob(jobId, setJob); const u2 = subscribeStops(jobId, setStops); return () => { u1(); u2(); }; }, [jobId]);
  if (!job) return <div className="p-20 text-center text-slate-500">Loading report…</div>;

  const counts = stops.reduce((a, s) => { a[s.status] = (a[s.status] || 0) + 1; return a; }, {});

  return (
    <div className="bg-white min-h-screen">
      <style>{`@media print { .no-print { display:none !important; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`}</style>
      <div className="no-print bg-slate-100 border-b border-slate-200">
        <div className="container-518 py-4 flex items-center justify-between">
          <Link to={`/admin/deliveries/${jobId}`} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600"><ArrowLeft className="w-4 h-4" /> Back to job</Link>
          <Button onClick={() => window.print()} data-testid="download-pdf" className="bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-11 px-6 font-semibold"><Printer className="w-4 h-4 mr-2" /> Download PDF</Button>
        </div>
      </div>

      <div className="container-518 py-10 max-w-4xl" data-testid="report-body">
        <div className="flex items-center justify-between border-b-2 border-[#0f2847] pb-4 mb-6">
          <div>
            <div className="font-display text-2xl font-extrabold text-[#0f2847]">518 Courier</div>
            <div className="text-sm text-slate-500">Proof of Delivery Report · OBLED Corporation d/b/a 518 Courier</div>
          </div>
          <div className="text-right text-sm">
            <div className="font-bold text-[#0f2847]">{job.job_reference}</div>
            <div className="text-slate-500">{job.job_date}</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-6 text-sm">
          <div><span className="font-bold text-[#0f2847]">Client:</span> {job.client_name}</div>
          <div><span className="font-bold text-[#0f2847]">Job:</span> {job.job_name}</div>
          <div><span className="font-bold text-[#0f2847]">Contract amount:</span> ${Number(job.contract_amount || 0).toFixed(2)}</div>
          <div><span className="font-bold text-[#0f2847]">Pickup:</span> {job.pickup_address}</div>
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          <span className="px-3 py-1.5 rounded-lg bg-slate-100 text-sm font-bold text-[#0f2847]">Total: {stops.length}</span>
          <span className="px-3 py-1.5 rounded-lg bg-emerald-100 text-sm font-bold text-emerald-700">Delivered: {counts.delivered || 0}</span>
          <span className="px-3 py-1.5 rounded-lg bg-amber-100 text-sm font-bold text-amber-800">Attempted: {counts.attempted || 0}</span>
          <span className="px-3 py-1.5 rounded-lg bg-red-100 text-sm font-bold text-red-700">Failed: {counts.failed || 0}</span>
          <span className="px-3 py-1.5 rounded-lg bg-slate-100 text-sm font-bold text-slate-600">Pending: {counts.pending || 0}</span>
        </div>

        <div className="space-y-4">
          {stops.map((s) => {
            const m = STOP_STATUS_META[s.status] || STOP_STATUS_META.pending;
            return (
              <div key={s.id} className="border border-slate-200 rounded-xl p-4 break-inside-avoid">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-bold text-[#0f2847]">Stop {String(s.stop_number).padStart(2, "0")} · {s.package_reference}</div>
                    <div className="text-sm text-[#0f2847]">{s.recipient_name}{s.company_name ? ` — ${s.company_name}` : ""}</div>
                    <div className="text-xs text-slate-500">{s.address_line_1}{s.address_line_2 ? `, ${s.address_line_2}` : ""}, {s.city}, {s.state} {s.zip_code}</div>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${m.cls}`}>{m.label}</span>
                </div>
                <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-600">
                  <div><span className="font-semibold">Delivery time:</span> {fmt(s.delivered_time)}</div>
                  <div><span className="font-semibold">Received by:</span> {s.recipient_received_by || "—"}</div>
                  <div><span className="font-semibold">GPS:</span> {s.latitude ? `${s.latitude.toFixed(5)}, ${s.longitude.toFixed(5)}` : "—"}</div>
                  <div><span className="font-semibold">Notes:</span> {s.driver_notes || "—"}</div>
                </div>
                {(() => {
                  const ph = (s.photos && s.photos.length) ? s.photos : (s.photo_url ? [{ url: s.photo_url }] : []);
                  if (!ph.length && !s.signature_url) return null;
                  return (
                    <div className="mt-3 flex flex-wrap gap-3">
                      {ph.map((p, i) => <img key={i} src={p.url} alt={`POD ${i + 1}`} className="h-28 rounded-lg border border-slate-200 object-cover" />)}
                      {s.signature_url && <img src={s.signature_url} alt="Signature" className="h-28 rounded-lg border border-slate-200 bg-white object-contain" />}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
